// Narrative reports (SPEC-v2 R1-R3; API.md 7.8, endpoints 81-84).
// The composed document is a GeneratedDocument (./documents.ts).
import { z } from "zod";
import { REPORT_STATUSES, type ContractStatus, type ReportStatus } from "../enums";
import { LIMITS } from "../limits";
import { type ActorRef, type UserRef, isoDate, requiredText } from "./common";
import { blockSchema, type TeamSection } from "./tors";

// ---------- Sections ----------

/** The three sections the team writes, in this order, always present (ARCHITECTURE 15.2). */
export const REPORT_TEAM_SECTIONS = [
  { key: "summary", title: "Summary of the period" },
  { key: "challenges", title: "Challenges and lessons learned" },
  { key: "next_period", title: "Plans for the next period" },
] as const;
export type ReportSectionKey = (typeof REPORT_TEAM_SECTIONS)[number]["key"];

const FIXED_KEYS: readonly string[] = REPORT_TEAM_SECTIONS.map((s) => s.key);

export const reportSectionSchema = z.object({
  key: z
    .string()
    .refine(
      (k) => FIXED_KEYS.includes(k) || /^custom-[0-9a-f-]{36}$/i.test(k),
      'A report section key is "summary", "challenges", "next_period" or "custom-<uuid>".',
    ),
  title: requiredText(LIMITS.sectionTitle, "Section title"),
  blocks: z.array(blockSchema).max(LIMITS.blocksPerSection, "At most 50 text blocks per section."),
});

export const reportSectionsSchema = z
  .array(reportSectionSchema)
  .max(LIMITS.sectionsMax, `At most ${LIMITS.sectionsMax} sections.`);

// ---------- Requests ----------

/** 81. POST /api/contracts/:id/reports */
export const createReportBody = z
  .object({
    title: requiredText(LIMITS.reportTitle, "Title"),
    periodFrom: isoDate,
    periodTo: isoDate,
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.periodTo < v.periodFrom) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["periodTo"], message: "The end of the period must be on or after its start." });
    }
  });
export type CreateReportBody = z.infer<typeof createReportBody>;

/**
 * 83. PATCH /api/reports/:id. Text fields only while draft; `status` by the
 * lead. The web autosaves by sending only `sections`. When only one of the
 * period dates is sent, the API checks the order against the stored one.
 */
export const updateReportBody = z
  .object({
    title: requiredText(LIMITS.reportTitle, "Title").optional(),
    periodFrom: isoDate.optional(),
    periodTo: isoDate.optional(),
    sections: reportSectionsSchema.optional(),
    status: z.enum(REPORT_STATUSES).optional(),
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.periodFrom && v.periodTo && v.periodTo < v.periodFrom) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["periodTo"], message: "The end of the period must be on or after its start." });
    }
  });
export type UpdateReportBody = z.infer<typeof updateReportBody>;

// ---------- Responses ----------

export type ReportSummary = {
  id: string;
  title: string;
  periodFrom: string;
  periodTo: string;
  status: ReportStatus;
  finalisedAt: string | null;
  updatedAt: string;
};

export type ReportDetail = ReportSummary & {
  contract: { id: string; name: string; reference: string; status: ContractStatus; lead: UserRef | null };
  /** summary, challenges, next_period, custom-… (same shape as the ToR's TeamSection) */
  sections: TeamSection[];
  finalisedBy: ActorRef | null;
  createdAt: string;
  can: { edit: boolean; finalise: boolean; reopen: boolean; delete: boolean; export: boolean };
};
